import { isUnlocked, totalStars, type Progress } from "../game/progress";
import { LISTED, type Restaurant } from "../game/restaurants";
import type { Settings } from "../game/settings";
import { money, plural } from "./format";
import { SettingsPanel } from "./SettingsPanel";
import { Stars } from "./Stars";

// The home screen: your restaurants, easiest first. The stars you earn at
// one open the next.

export function RestaurantList({
  progress,
  onPick,
  settings,
  onSettings,
}: {
  progress: Progress;
  onPick: (r: Restaurant) => void;
  settings: Settings;
  onSettings: (s: Settings) => void;
}) {
  const stars = totalStars(progress);
  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:py-10">
      <header className="mb-6 text-center">
        <h1 className="text-4xl font-black tracking-tight sm:text-5xl">
          <span aria-hidden="true">🍔 </span>Order Up!
        </h1>
        <p className="mt-2 text-ink-soft">Cook it, serve it, wash up after it. Then do it all again, faster.</p>
      </header>

      <dl className="mb-6 grid grid-cols-3 gap-2 rounded-2xl bg-panel p-3 text-center shadow-sm">
        <div>
          <dt className="text-xs font-semibold tracking-wide text-ink-soft uppercase">Stars</dt>
          <dd className="text-xl font-bold">
            <span className="text-sunny" aria-hidden="true">★ </span>
            {stars}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-semibold tracking-wide text-ink-soft uppercase">Days worked</dt>
          <dd className="text-xl font-bold">{progress.shifts}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold tracking-wide text-ink-soft uppercase">Earned</dt>
          <dd className="text-xl font-bold">{money(progress.earnings)}</dd>
        </div>
      </dl>

      <h2 className="mb-3 text-sm font-bold tracking-wide text-ink-soft uppercase">Restaurants</h2>
      <ul className="space-y-3">
        {LISTED.map((r) => (
          <RestaurantCard key={r.id} restaurant={r} progress={progress} totalStars={stars} onPick={onPick} />
        ))}
      </ul>

      <details className="mt-8 rounded-2xl bg-panel p-4 shadow-sm">
        <summary className="cursor-pointer text-sm font-bold tracking-wide text-ink-soft uppercase">Sound & controls</summary>
        <div className="mt-3">
          <SettingsPanel settings={settings} onChange={onSettings} />
        </div>
      </details>
    </div>
  );
}

function RestaurantCard({
  restaurant: r,
  progress,
  totalStars,
  onPick,
}: {
  restaurant: Restaurant;
  progress: Progress;
  totalStars: number;
  onPick: (r: Restaurant) => void;
}) {
  const open = isUnlocked(r, progress);
  const best = progress.best[r.id];
  const workable = open && r.setup !== null;
  return (
    <li className={`rounded-2xl border-2 p-4 ${workable ? "border-accent bg-panel shadow-sm" : "border-dashed border-steel bg-tile/50"}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          {r.place && <p className="text-xs font-bold tracking-wide text-accent uppercase">{r.place}</p>}
          <h3 className="text-lg font-bold">{r.name}</h3>
          <p className="text-sm font-semibold text-ink-soft">{r.tagline}</p>
        </div>
        {workable && <Stars count={best?.stars ?? 0} className="text-2xl" />}
      </div>
      <p className={`mt-2 text-sm ${workable ? "" : "text-ink-soft"}`}>{r.blurb}</p>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        {workable ? (
          <>
            <span className="text-sm text-ink-soft">{best ? `Best day ${money(best.pay)}` : "Not opened yet"}</span>
            <button
              type="button"
              onClick={() => onPick(r)}
              aria-label={`Open up ${r.name}`}
              className="rounded-xl bg-accent px-4 py-2 font-bold text-panel shadow hover:brightness-110 focus-visible:outline-4 focus-visible:outline-accent-soft"
            >
              Open up
            </button>
          </>
        ) : (
          <span className="text-sm font-semibold text-ink-soft">
            {open ? (
              "Coming soon"
            ) : (
              <>
                <span aria-hidden="true">🔒 </span>
                Earn {plural(r.unlockStars - totalStars, "more star")} to unlock
                {r.setup === null && " · coming soon"}
              </>
            )}
          </span>
        )}
      </div>
    </li>
  );
}
